/* LEGEND — floating position card. Ported from card.jsx. */
import React from 'react';
import { Icon } from './Widgets';
import { fmtQty, fmtPx, fmtPx4, fmtPct, pnl, inProfit } from '../lib/format';
import { entryChipLabel } from '../lib/mappers';
import { BUCKETS, bucketShares, TARGET_PRESETS, TRAIL_SOURCES, splitLabel } from '../lib/constants';

const { useState: useStateC, useRef: useRefC, useEffect: useEffectC } = React;

export function FloatingCard(props: any) {
  const { position, symbol, lastPrice, levels, pos, collapsed, sizeParams, docked,
    onMove, onToggle, onClose, onBuyNow, onCloseNow, onSellPercent,
    preset, trailing, trailSource, indSnap, onApplyPreset, onToggleTrailing, onApplyTrailSource, side } = props;
  // The side toggle is locked while a position is open (enforced in the header,
  // in Terminal.setTradeSide and again in the worker), so whenever a position
  // exists the armed side and position.side are the same. `isShort` drives the
  // ENTRY row, which is only reachable while flat; `coverMode` below reads
  // position.side because that is the authority for an open position.
  const armedSide = side === 'short' ? 'short' : 'long';
  const isShort = armedSide === 'short';
  const [armed, setArmed] = useStateC(false);
  const [flash, setFlash] = useStateC<any>(null);
  const armTimer = useRefC<any>(null);

  const has = position && position.qty > 0;
  const up = has ? inProfit(position.side, position.avg, lastPrice) : false;
  const pl = has ? pnl(position.side, position.avg, lastPrice, position.qty) : null;
  const mktVal = has ? lastPrice * position.qty : 0;

  function startDrag(e: any) {
    if (e.target.closest('button')) return;
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, ox = pos.x, oy = pos.y;
    const move = (ev: any) => onMove({ x: ox + (ev.clientX - sx), y: oy + (ev.clientY - sy) });
    const upf = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', upf); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', upf);
  }

  function buy(b: any) {
    setFlash(b.key); setTimeout(() => setFlash(null), 500);
    onBuyNow(b);
  }
  function closeNow() {
    if (!armed) {
      setArmed(true);
      armTimer.current = setTimeout(() => setArmed(false), 2600);
      return;
    }
    clearTimeout(armTimer.current); setArmed(false); onCloseNow();
  }
  useEffectC(() => () => clearTimeout(armTimer.current), []);

  // Buy Now buckets — rendered ABOVE the key levels so the market-buy row isn't
  // adjacent to the card edge (avoids accidental buys when scrolling/scaling).
  const buyNowRow = React.createElement('div', { className: 'pc-foot' },
    React.createElement('div', { className: 'buynow-row' + (isShort ? ' short' : '') },
      React.createElement('span', { className: 'bn-label' }, isShort ? 'Short Now' : 'Buy Now'),
      React.createElement('div', { className: 'mini-buckets' },
        BUCKETS.map((b) => React.createElement('button', {
          key: b.key, className: 'mini-bucket' + (isShort ? ' short' : '') + (flash === b.key ? ' on flash-ok' : ''),
          title: fmtQty(bucketShares(b.key, lastPrice, sizeParams)) + (isShort ? ' sh short at market' : ' sh at market'),
          onClick: () => buy(b),
        }, b.name)))));

  // Trim + Close Now — stay at the bottom of the card (below key levels).
  // Reducing a SHORT is a cover, not a sell — selling more would add to it.
  // Reads position.side: the position is the authority for what an exit does.
  const coverMode = has && position.side === 'short';
  const trimClose = has ? React.createElement('div', { className: 'pc-foot' },
    React.createElement('div', { className: 'buynow-row' + (coverMode ? ' cover' : '') },
      // 'Sell' / 'Cover' rather than a neutral 'Trim': the label should say which
      // way the order goes, because on a short the two are opposites.
      React.createElement('span', { className: 'bn-label sell' }, coverMode ? 'Cover' : 'Sell'),
      React.createElement('div', { className: 'mini-buckets' },
        [10, 25, 50, 75].map((pct) => React.createElement('button', {
          key: 'trim' + pct, className: 'mini-bucket' + (coverMode ? ' cover' : ''),
          title: (coverMode ? 'Cover ' : 'Sell ') + pct + '% of ' + fmtQty(position.qty) + ' @ mkt',
          onClick: () => onSellPercent(pct),
        }, pct + '%')))),
    React.createElement('button', {
      className: 'btn btn-close' + (armed ? ' danger-armed' : ''), onClick: closeNow,
    }, armed
      ? 'Confirm — ' + (coverMode ? 'cover ' : 'close ') + fmtQty(position.qty) + ' @ mkt'
      : (coverMode ? 'Cover all (100%)' : 'Close all (100%)'))) : null;

  // Exit strategy (target preset + trailing) — shown ALWAYS, even when flat, so the
  // symbol's configured preset/trailing is always visible and editable pre-entry.
  const trailSrc = trailSource || 'levels';
  const trailDef = TRAIL_SOURCES.find((s) => s.key === trailSrc);
  const exitStrategy = React.createElement('div', { className: 'pc-section' },
    React.createElement('div', { className: 'sec-title' }, 'Exit strategy'),
    React.createElement('div', { className: 'mini-buckets' },
      TARGET_PRESETS.map((ps) => React.createElement('button', {
        key: ps.key, className: 'mini-bucket' + (preset === ps.key ? ' on' : ''),
        title: ps.splits.length ? (ps.splits.length + ' targets · ' + splitLabel(ps.splits)) : 'No take-profits — stop only', onClick: () => onApplyPreset(ps.key),
      }, ps.label))),
    React.createElement('div', { className: 'trail-row', onClick: onToggleTrailing },
      React.createElement('div', null,
        React.createElement('div', { className: 'tr-label' }, 'Trailing stop'),
        React.createElement('div', { className: 'tr-sub mono' },
          trailing ? 'following ' + (trailDef ? trailDef.label.toLowerCase() : trailSrc) : 'fixed')),
      React.createElement('span', { className: 'toggle' + (trailing ? ' on' : '') })),
    // What the trail follows — worker moves the stop on each completed 1m bar.
    // Selectable any time (also while OFF: the choice is recorded for next enable).
    // Each button shows the live 1m value of its line (worker-published snapshot —
    // the exact price the ratchet would anchor to, NOT the chart's per-TF lines).
    // Sources the worker's convergence gate would refuse (< period bars of 1m
    // history) render dimmed: pickable, but the trail will hold until they're real.
    onApplyTrailSource ? React.createElement('div', { className: 'trail-src-grid' },
      TRAIL_SOURCES.map((s) => {
        const val = trailSourceValue(indSnap, s.key);
        const ready = trailSourceReady(indSnap, s.key, val);
        return React.createElement('button', {
          key: s.key,
          className: 'trail-src' + (trailSrc === s.key ? ' on' : '') + (ready ? '' : ' dim'),
          title: s.title + (ready ? '' : ' — not converged yet (too little 1m history)'),
          onClick: () => onApplyTrailSource(s.key),
        },
          React.createElement('span', { className: 'ts-name' }, s.label),
          React.createElement('span', { className: 'ts-val' },
            val != null ? '$' + fmtPx(val) : (s.key === 'levels' ? 'level map' : '—')));
      })) : null);

  // Targets — rendered BELOW the Close Now row so target fills/updates never
  // shift the Buy/Trim/Close buttons while the user is clicking them.
  const targetsSection = (has && position.targets && position.targets.length)
    ? React.createElement('div', { className: 'pc-section', key: 'targets' },
      React.createElement('div', { className: 'sec-title' }, 'Targets'),
      position.targets.map((t: any) => {
        // Estimated PnL if this target fills (matches the chart overlay pill).
        const dir = position.side === 'long' ? 1 : -1;
        const est = (t.px - position.avg) * dir * t.qty;
        return React.createElement('div', { key: t.id, className: 'lvl-row' },
          React.createElement('span', { className: 'lv-tag t' }, t.t),
          React.createElement('span', { className: 'lv-px num' }, '$' + fmtPx4(t.px)),
          React.createElement('span', { className: 'lv-qty' }, '×' + fmtQty(t.qty)),
          React.createElement('span', { className: 'lv-pnl num ' + (est >= 0 ? 'up' : 'down') },
            (est >= 0 ? '+$' : '−$') + Math.abs(est).toLocaleString('en-US', { maximumFractionDigits: 0 })));
      }))
    : null;

  // Pending entry orders (resting buy-dip / buy-on-break) — shown even when flat.
  const pendingOrders = (position && position.entries && position.entries.length)
    ? React.createElement('div', { className: 'pc-section', key: 'pending' },
      React.createElement('div', { className: 'sec-title' }, 'Pending orders'),
      position.entries.map((en: any) => React.createElement('div', { key: en.id, className: 'lvl-row' + (en.pending ? ' is-pending' : '') },
        // Side-aware, like the chart chip. Rendered blind, a resting short above
        // the market read green 'BRK' here while the chip on the chart read
        // 'POP' — the same order, two labels, one of them the long-side word.
        React.createElement('span', { className: 'lv-tag ' + (en.kind === 'dip' ? 's' : 't') },
          entryChipLabel(en.kind, armedSide)),
        React.createElement('span', { className: 'lv-px num' }, '$' + fmtPx4(en.px)),
        React.createElement('span', { className: 'lv-qty' }, '×' + fmtQty(en.shares)))))
    : null;

  return React.createElement('div', {
    className: 'pos-card' + (collapsed ? ' collapsed' : '') + (docked ? ' docked' : ''),
    style: docked ? undefined : { left: pos.x + 'px', top: pos.y + 'px' },
  },
    React.createElement('div', { className: 'pc-head', onPointerDown: docked ? undefined : startDrag },
      React.createElement('span', { className: 'pc-sym' }, symbol),
      has ? React.createElement('span', { className: 'side-tag ' + position.side }, position.side)
        : React.createElement('span', { className: 'muted', style: { fontSize: 11 } }, isShort ? 'flat · short' : 'flat'),
      React.createElement('div', { className: 'pc-actions' },
        React.createElement('button', { className: 'icon-btn sm', onClick: onToggle, title: collapsed ? 'Expand' : 'Collapse' }, Icon.chev()),
        React.createElement('button', { className: 'icon-btn sm', onClick: onClose, title: 'Close card' }, Icon.x()))),

    has ? React.createElement('div', { className: 'pc-body' },
      React.createElement('div', { className: 'pnl-block ' + (up ? 'up' : 'down') },
        React.createElement('span', { className: 'pnl-main num' }, (pl!.dollar >= 0 ? '+$' : '−$') + Math.abs(pl!.dollar).toLocaleString('en-US', { maximumFractionDigits: 0 })),
        React.createElement('span', { className: 'pnl-pct num' }, fmtPct(pl!.pct))),
      React.createElement('div', { className: 'stat-grid' },
        stat('Qty', fmtQty(position.qty)),
        stat('Avg cost', '$' + fmtPx4(position.avg)),
        stat('Last', '$' + fmtPx4(lastPrice)),
        stat('Mkt value', '$' + mktVal.toLocaleString('en-US', { maximumFractionDigits: 0 }))),

      position.stop ? React.createElement('div', { className: 'pc-section' },
        React.createElement('div', { className: 'sec-title' }, 'Stop loss', trailing ? React.createElement('span', { className: 'sec-flag' }, 'trailing') : null),
        React.createElement('div', { className: 'lvl-row' },
          React.createElement('span', { className: 'lv-tag s' }, trailing ? 'TRAIL' : 'STOP'),
          React.createElement('span', { className: 'lv-px num' }, '$' + fmtPx4(position.stop.px)),
          React.createElement('span', { className: 'lv-qty' }, '×' + fmtQty(position.stop.qty)))) : null,

      pendingOrders,

      exitStrategy,

      buyNowRow,
      keyLevels(levels, indSnap),
      trimClose,
      targetsSection
    ) : React.createElement('div', { className: 'pc-body' },
      React.createElement('div', { className: 'pc-empty' },
        React.createElement('div', { className: 'pe-ic' }, Icon.bolt()),
        React.createElement('div', { className: 'pe-t' }, 'No open position'),
        React.createElement('div', { className: 'pe-s' }, isShort
          ? 'Click the chart to rest a Pop / Breakdown order, or Short Now at market.'
          : 'Click the chart to rest a Dip / Break order, or Buy Now at market.')),
      pendingOrders,
      exitStrategy,
      buyNowRow,
      keyLevels(levels, indSnap)));
}

function stat(k: string, v: string) {
  return React.createElement('div', { className: 'stat', key: k },
    React.createElement('span', { className: 'k' }, k),
    React.createElement('span', { className: 'v num' }, v));
}

const TRAIL_EMA_PERIOD: Record<string, number> = { ema9: 9, ema21: 21, ema50: 50, ema200: 200 };

// Live 1m value of a trail source from the worker's indicator snapshot (null when
// unknown — no snapshot yet, or the indicator hasn't seeded on this symbol).
function trailSourceValue(snap: any, key: string): number | null {
  if (!snap || key === 'levels') return null;
  const v = Number(snap[key]);
  return Number.isFinite(v) && v > 0 ? v : null;
}

// Mirrors the worker's convergence gate: an EMA needs >= period bars of 1m history
// before the engine trusts the line. Unknown bar count (REST-seeded snapshot has
// none) → don't grey; no snapshot at all → plain labels, nothing dimmed.
function trailSourceReady(snap: any, key: string, val: number | null): boolean {
  if (key === 'levels') return true;
  if (!snap) return true;
  if (val == null) return false;
  const period = TRAIL_EMA_PERIOD[key];
  if (!period) return true; // vwap: a present value is ready
  const bars = Number(snap.bars);
  return Number.isFinite(bars) ? bars >= period : true;
}

/* ============================================================
   EMBEDDED POSITION BAR — welded to the bottom of the chart,
   below the volume. One horizontal trade rail: identity · P&L ·
   live stats · size buckets · scale-out · close. Always present.
   Ported from legend/card.jsx; sells go through onSellPercent
   (whole-number %, worker resolves qty live — same as the card).
   ============================================================ */
const PB_SELL_PCTS = [25, 50, 75];

export function PositionBar(props: any) {
  const { position, symbol, lastPrice, sizeParams, onBuyNow, onCloseNow, onSellPercent, onExpand, expanded, side } = props;
  // Same rule as the card: the toggle cannot move while a position is open, so
  // these agree whenever one exists. `isShort` drives the entry row (flat only),
  // `coverMode` the exit row (position only).
  const isShort = side === 'short';
  const [armed, setArmed] = useStateC(false);
  const [flash, setFlash] = useStateC<any>(null);
  const armTimer = useRefC<any>(null);
  const has = position && position.qty > 0;
  const coverMode = has && position.side === 'short';
  const up = has ? inProfit(position.side, position.avg, lastPrice) : false;
  const pl = has ? pnl(position.side, position.avg, lastPrice, position.qty) : null;
  const mktVal = has ? lastPrice * position.qty : 0;

  function buy(b: any) { setFlash(b.key); setTimeout(() => setFlash(null), 500); onBuyNow(b); }
  function sell(p: number) { setFlash('sell' + p); setTimeout(() => setFlash(null), 500); onSellPercent(p); }
  function closeNow() {
    if (!armed) { setArmed(true); armTimer.current = setTimeout(() => setArmed(false), 2600); return; }
    clearTimeout(armTimer.current); setArmed(false); onCloseNow();
  }
  useEffectC(() => () => clearTimeout(armTimer.current), []);

  return React.createElement('div', { className: 'chart-posbar' + (has ? '' : ' flat') },
    onExpand ? React.createElement('button', {
      className: 'pb-expand' + (expanded ? ' on' : ''), onClick: onExpand,
      title: expanded ? 'Hide full position card' : 'Open full position card',
    }, Icon.chev()) : null,
    React.createElement('div', { className: 'pb-id' },
      React.createElement('span', { className: 'pb-sym' }, symbol),
      has ? React.createElement('span', { className: 'side-tag ' + position.side }, position.side)
        : React.createElement('span', { className: 'pb-flat-tag' + (isShort ? ' short' : '') }, isShort ? 'flat · short' : 'flat')),

    has ? React.createElement('div', { className: 'pb-pnl ' + (up ? 'up' : 'down') },
      React.createElement('span', { className: 'pb-pnl-main num' }, (pl!.dollar >= 0 ? '+$' : '−$') + Math.abs(pl!.dollar).toLocaleString('en-US', { maximumFractionDigits: 0 })),
      React.createElement('span', { className: 'pb-pnl-pct num' }, fmtPct(pl!.pct)))
      : React.createElement('div', { className: 'pb-flat-msg' }, 'No open position — click the chart to rest an order, or size in at market'),

    has ? React.createElement('div', { className: 'pb-stats' },
      pbStat('Qty', fmtQty(position.qty)),
      pbStat('Avg', '$' + fmtPx4(position.avg)),
      pbStat('Last', '$' + fmtPx4(lastPrice)),
      pbStat('Mkt val', '$' + mktVal.toLocaleString('en-US', { maximumFractionDigits: 0 }))) : null,

    React.createElement('div', { className: 'pb-spacer' }),

    React.createElement('div', { className: 'pb-actions' },
      has ? React.createElement('div', { className: 'pb-group sell-group' + (coverMode ? ' cover' : '') },
        React.createElement('span', { className: 'pb-glabel sell' }, coverMode ? 'Cover' : 'Sell'),
        PB_SELL_PCTS.map((p) => React.createElement('button', {
          key: p, className: 'mini-bucket ' + (coverMode ? 'cover' : 'sell') + (flash === 'sell' + p ? ' on flash-sell' : ''),
          title: (coverMode ? 'Cover ' : 'Sell ') + p + '% of ' + fmtQty(position.qty) + ' @ mkt',
          onClick: () => sell(p),
        }, p + '%'))) : null,
      React.createElement('div', { className: 'pb-group buy-group' + (isShort ? ' short' : '') },
        React.createElement('span', { className: 'pb-glabel' }, isShort ? 'Short' : 'Buy'),
        BUCKETS.map((b) => React.createElement('button', {
          key: b.key, className: 'mini-bucket' + (isShort ? ' short' : '') + (flash === b.key ? ' on flash-ok' : ''),
          title: fmtQty(bucketShares(b.key, lastPrice, sizeParams)) + (isShort ? ' sh short at market' : ' sh at market'),
          onClick: () => buy(b),
        }, b.name))),
      has ? React.createElement('button', {
        className: 'btn btn-close pb-close' + (armed ? ' danger-armed' : ''), onClick: closeNow,
      }, armed
        ? 'Confirm ' + (coverMode ? 'cover ' : 'close ') + fmtQty(position.qty)
        : (coverMode ? 'Cover' : 'Close')) : null));
}

function pbStat(k: string, v: string) {
  return React.createElement('div', { className: 'pb-stat', key: k },
    React.createElement('span', { className: 'pbs-k' }, k),
    React.createElement('span', { className: 'pbs-v num' }, v));
}

function keyLevels(levels: any, indSnap?: any) {
  if (!levels) return null;
  // VWAP: prefer the worker/screener session VWAP (04:00 anchor — the number the
  // trailing stop actually anchors to). levels.vwap is computed client-side over
  // ONLY the chart's loaded candle window, so it drifts with the visible range
  // (JWEL 2026-08-10: window-VWAP 4.27 vs true session VWAP 3.99).
  const sessionVwap = indSnap != null && Number.isFinite(Number(indSnap.vwap)) && Number(indSnap.vwap) > 0
    ? Number(indSnap.vwap)
    : levels.vwap;
  const items = [['Day high', levels.dayHigh], ['Day low', levels.dayLow], ['PM high', levels.pmHigh], ['VWAP', sessionVwap], ['Prev close', levels.prevClose]];
  return React.createElement('div', { className: 'pc-section' },
    React.createElement('div', { className: 'sec-title' }, 'Key levels'),
    React.createElement('div', { className: 'keylvl-grid' },
      items.map(([k, v]: any) => React.createElement('div', { className: 'keylvl', key: k },
        React.createElement('span', { className: 'kk' }, k),
        React.createElement('span', { className: 'kv num' }, '$' + fmtPx(v))))));
}
